import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import AuthHeader from '../../components/AuthHeader'
import { api } from '../../utils/api'
import toast from 'react-hot-toast'

const VerifyEmailPage = () => {
  const [loading, setLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const { resendVerification } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    // Get email from navigation state or URL params
    const emailFromState = location.state?.email
    const emailFromParams = searchParams.get('email')
    const messageFromState = location.state?.message

    if (emailFromState) {
      setEmail(emailFromState)
    } else if (emailFromParams) {
      setEmail(emailFromParams)
    }

    if (messageFromState) {
      setMessage(messageFromState)
    }

    // Check if there's a token in the URL (user clicked email link)
    const token = searchParams.get('token')
    if (token) {
      handleEmailVerification(token)
    }
  }, [location.state, searchParams])

  const handleEmailVerification = async (token) => {
    setLoading(true)
    try {
      const response = await api.get(`/auth/verify-email/${token}`)
      
      toast.success('Email verified successfully! You can now log in.')
      navigate('/login', { 
        state: { 
          message: 'Email verified successfully! You can now log in.' 
        } 
      })
    } catch (error) {
      console.error('Email verification error:', error)
      const errorMessage = error.response?.data?.message || 'Email verification failed'
      
      // Handle specific error cases
      if (error.response?.status === 400) {
        if (errorMessage.includes('already verified')) {
          toast.success('Your email is already verified! You can log in now.')
          navigate('/login', { 
            state: { 
              message: 'Your email is already verified! You can log in now.' 
            } 
          })
        } else if (errorMessage.includes('Invalid or expired')) {
          toast.error('This verification link is invalid or has expired. Please request a new one.')
          setMessage('This verification link is invalid or has expired. Please request a new verification email.')
        } else {
          toast.error(errorMessage)
        }
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Email address is required')
      return
    }

    setResendLoading(true)
    try {
      const result = await resendVerification(email)
      
      if (result.success) {
        toast.success(result.message || 'Verification email sent!')
      } else {
        toast.error(result.error)
      }
    } catch (error) {
      toast.error('Failed to resend verification email')
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-black">
      <div className='p-4'>
        <div className="border-2 border-white">
          <AuthHeader />
          <div className='bg-[#141414] p-4 w-full min-h-screen flex items-center justify-center'>
            <div className="w-full max-w-sm relative">

              {/* Form Card */}
              <div className="bg-card border border-gray-700 p-6 relative">

                <div className="space-y-5 flex flex-col gap-4">
                  <div className='flex justify-center items-center !mb-4'>
                    <h4>
                      Check Your Email!
                    </h4>
                  </div>

                  {message && (
                    <div className="mb-4 p-3 bg-green-900/20 border border-green-500/30 rounded">
                      <p className="text-green-400 text-sm text-center">{message}</p>
                    </div>
                  )}

                  <div className="text-center space-y-3">
                    <div className="w-16 h-16 mx-auto bg-red-600 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-white text-sm">
                      We've sent a verification link to your email address
                    </p>
                    {email && (
                      <p className="text-gray-400 text-xs">
                        Sent to: <span className="text-white">{email}</span>
                      </p>
                    )}
                    <p className="text-gray-400 text-xs">
                      Didn't receive the email? Check your spam folder
                    </p>
                  </div>

                  <div className='!mx-6'>
                    <button
                      onClick={handleResendVerification}
                      disabled={resendLoading || !email}
                      className="w-full h-10 bg-red-600 hover:bg-red-700 disabled:bg-red-700 disabled:cursor-not-allowed text-white font-bold text-sm rounded-lg transition-colors flex items-center justify-center uppercase tracking-wide"
                    >
                      {resendLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Sending...</span>
                        </div>
                      ) : (
                        'RESEND EMAIL'
                      )}
                    </button>
                  </div>
                </div>

                <div className="mt-6 text-center space-y-2">
                  <p className="text-xs text-gray-400">
                    Already verified?{' '}
                    <Link 
                      to="/login" 
                      className="text-red-400 hover:text-red-300 transition-colors font-medium"
                    >
                      Back to Login
                    </Link>
                  </p>
                  <p className="text-xs text-gray-400">
                    Need help?{' '}
                    <a 
                      href="mailto:support@thelaunchpad.com" 
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      Contact Support
                    </a>
                  </p>
                </div>

                {/* Privacy & Terms Links */}
                <div className="mt-6 pt-4 border-t border-gray-700 flex flex-wrap justify-center gap-3 text-xs">
                  <Link
                    to="/privacy-policy"
                    className="text-gray-400 hover:text-red-300 transition-colors font-avenir"
                  >
                    Privacy Policy
                  </Link>
                  <span className="text-gray-600">|</span>
                  <Link
                    to="/terms-of-use"
                    className="text-gray-400 hover:text-red-300 transition-colors font-avenir"
                  >
                    Terms of Use
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VerifyEmailPage
